import { pct } from "@/lib/format";

const proKey = (p) => p.id || p.professional_id;
const sum = (a, k) => a.reduce((s, x) => s + (Number(x[k]) || 0), 0);

// Per-professional performance from live collections.
export function computeProMetrics(pros, leads, cons, projects) {
  const leadsByPro = {}, consByPro = {}, projByPro = {};
  leads.forEach((l) => { const k = l.professional_id; if (k) (leadsByPro[k] ||= []).push(l); });
  cons.forEach((c) => { const k = c.professional_id; if (k) (consByPro[k] ||= []).push(c); });
  projects.forEach((p) => {
    const ids = Array.isArray(p.professional_ids) && p.professional_ids.length ? p.professional_ids : [p.professional_id].filter(Boolean);
    ids.forEach((id) => { if (id) (projByPro[id] ||= []).push(p); });
  });
  return pros.map((p) => {
    const id = proKey(p);
    const L = leadsByPro[id] || [];
    const responded = L.filter((l) => l.status === "responded" || l.responded_at).length;
    const C = consByPro[id] || [];
    const confirmed = C.filter((c) => c.professional_confirmed === true || c.status === "visit_verified").length;
    const cancelled = C.filter((c) => c.status === "cancelled" || c.customer_decision === "cancelled" || c.visit_progress === "cancelled").length;
    const P = projByPro[id] || [];
    const promise = P.filter((x) => x.brugo_promise).length;
    const delayed = P.filter((x) => ["at_risk", "delayed", "off_track"].includes(x.promise_status)).length;
    const completed = Number(p.completed_project_count || 0) || P.filter((x) => x.status === "completed").length;
    const responseRate = pct(responded, L.length);
    const rating = Number(p.rating_average || 0);
    return {
      id, name: p.full_name || p.business_name || id, cluster: p.cluster || "", category: p.category_name || "", city: p.city || "", status: p.status, rating,
      floated: L.length, responded, responseRate, totalVisits: C.length, confirmed, cancelled,
      projects: P.length, promise, normal: P.length - promise, delayed, completed,
      score: completed * 2 + promise * 3 + responseRate / 10 + rating,
    };
  });
}

export function withIncentives(metrics, clusters, rules) {
  const r = rules || {};
  const leadIds = new Set();
  (clusters || []).forEach((c) => (c.lead_pro_ids || []).forEach((id) => leadIds.add(id)));
  const byCluster = {};
  metrics.forEach((m) => { if (m.cluster) (byCluster[m.cluster] ||= []).push(m); });
  const topId = {}, maxPromiseId = {};
  Object.entries(byCluster).forEach(([cl, arr]) => {
    topId[cl] = arr.reduce((a, b) => (b.score > a.score ? b : a), arr[0]).id;
    maxPromiseId[cl] = arr.reduce((a, b) => (b.promise > a.promise ? b : a), arr[0]).id;
  });
  return metrics.map((m) => {
    let inc = 0;
    inc += (r.per_completed_job || 0) * m.completed;
    inc += (r.per_brugo_promise || 0) * m.promise;
    if (m.responseRate >= (r.response_rate_target || 0)) inc += (r.response_rate_bonus || 0);
    const isTop = !!m.cluster && topId[m.cluster] === m.id;
    const isMaxPromise = !!m.cluster && maxPromiseId[m.cluster] === m.id;
    const isLead = leadIds.has(m.id);
    if (isTop) inc += (r.top_pro_cluster_bonus || 0);
    if (isMaxPromise) inc += (r.max_promise_cluster_bonus || 0);
    if (isLead) inc += (r.cluster_lead_bonus || 0);
    return { ...m, isTop, isMaxPromise, isLead, incentive: inc };
  });
}

export function clusterAggregate(metrics) {
  const by = {};
  metrics.forEach((m) => { const k = m.cluster || "Unassigned"; (by[k] ||= []).push(m); });
  return Object.entries(by).map(([cluster, arr]) => ({
    cluster, pros: arr.length,
    floated: sum(arr, "floated"), responded: sum(arr, "responded"),
    responseRate: pct(sum(arr, "responded"), sum(arr, "floated")),
    confirmed: sum(arr, "confirmed"), cancelled: sum(arr, "cancelled"),
    promise: sum(arr, "promise"), normal: sum(arr, "normal"), delayed: sum(arr, "delayed"),
    completed: sum(arr, "completed"), incentive: sum(arr, "incentive"),
  })).sort((a, b) => b.pros - a.pros);
}
